import React, { useState, useMemo } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import fonts_config from "./fonts_config.json";
import LoaderGraph from "../../general/LoaderGraph";
import { useFormatter } from "./../../../util/formatter";
import { Button } from "react-bootstrap";
import { useSolarCalc } from "../../../util/calculations";
import GYLogo from "../../../images/BASE64/GYLogo";
import accSignature from "../../../images/BASE64/accSignature";
import usePeakLabels from "../../../util/peakLabels";
import rfdc from 'rfdc';

const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.fonts = fonts_config;

function BillingDetailsTemplate({ copyType='ORIGINAL', onDocDefinition=()=>{}, ...props }) {
	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const [invoiceName, setInvoiceName] = useState(null);
	const [formatNumber] = useFormatter();
	const solarCalc = useSolarCalc();
	const parseNo = solarCalc.parseFormatNumber;
	const user = props.user;

	const logo = GYLogo;
	const signImg = accSignature;

	const selectedSite = props.selectedSite;
	const country = selectedSite.value.country;
	const peakLabels = usePeakLabels(country);

	const copyTypes = useMemo(() => ({
		ORIGINAL: "Original (ต้นฉบับ)",
		COPY: 'Copy (สำเนา)'
	}), []);

	// Show signature for client access
	const showSignature = user.roles.includes(
		"administration:InvoiceTable:_clientViewInv"
	);

	// Signature object
	const signObj = {
		image: signImg,
		height: 50,
		width: 107,
		absolutePosition: { x: -300, y: -120 },
	};

	// Signature section object
	const signature = showSignature ? signObj : {};

	/**
	 * Get discount remark in language requested
	 * @param {Language} lang
	 * @param {Discount} discount
	 * @param {MEA/PEA} EAtariff
	 */
	const getDiscountRemark = (lang, discount, EAtariff) => {
		switch (lang) {
			case "TH":
				return ` (ส่วนลดอัตราค่าไฟ ${parseFloat(discount)}% จาก ${EAtariff})`;

			default:
				return ` (with ${parseFloat(discount)}% discount from ${EAtariff}'s Tariff)`;
		}
	};

	/**
	 * Get Tariff information in English and Thai language
	 * @param {Parameters of site} gyTariffParam
	 * @param {MEA/PEA} EAtariff
	 */
	const getGYTariff = (gyTariffParam, EAtariff) => {
		let ONpeakStr_eng = `${
			peakLabels[0]
		} (baht/kWh) = ${gyTariffParam.priceON_disc.toFixed(6)}`;
		let ONpeakStr_th = `ช่วงเวลา ${
			peakLabels[0]
		} (บาท/kWh) = ${gyTariffParam.priceON_disc.toFixed(6)}`;

		if (gyTariffParam.discountON > 0) {
			ONpeakStr_eng += getDiscountRemark(
				"ENG",
				parseFloat(formatNumber(gyTariffParam.discountON * 100, 2)),
				EAtariff[0]
			);
			ONpeakStr_th += getDiscountRemark(
				"TH",
				parseFloat(formatNumber(gyTariffParam.discountON * 100, 2)),
				EAtariff[1]
			);
		}

		let OFFpeakStr_eng = `${
			peakLabels[1]
		} (baht/kWh) = ${gyTariffParam.priceOFF_disc.toFixed(6)}`;
		let OFFpeakStr_th = `ช่วงเวลา ${
			peakLabels[1]
		} (บาท/kWh) = ${gyTariffParam.priceOFF_disc.toFixed(6)}`;

		if (gyTariffParam.discountOFF > 0) {
			OFFpeakStr_eng += getDiscountRemark(
				"ENG",
				parseFloat(formatNumber(gyTariffParam.discountOFF * 100, 2)),
				EAtariff[0]
			);
			OFFpeakStr_th += getDiscountRemark(
				"TH",
				parseFloat(formatNumber(gyTariffParam.discountOFF * 100, 2)),
				EAtariff[1]
			);
		}

		let FTStr_eng = "";
		let FTStr_th = "";
		if (gyTariffParam.discountFT > 0) {
			FTStr_eng = `FT (baht/kWh) = ${gyTariffParam.FTcost_disc.toFixed(6)}`;
			FTStr_th = `FT (บาท/kWh) = ${gyTariffParam.FTcost_disc.toFixed(6)}`;
			FTStr_eng += getDiscountRemark(
				"ENG",
				parseFloat(formatNumber(gyTariffParam.discountFT * 100, 2)),
				EAtariff[0]
			);
			FTStr_th += getDiscountRemark(
				"TH",
				parseFloat(formatNumber(gyTariffParam.discountFT * 100, 2)),
				EAtariff[1]
			);
		}

		return {
			ONpeakStr_eng,
			ONpeakStr_th,
			OFFpeakStr_eng,
			OFFpeakStr_th,
			FTStr_eng,
			FTStr_th,
		};
	};

	/**
	 * Set PDF name to be downloaded
	 * @param {Selected site} site
	 */
	const setBDname = (site) => {
		const invName = `${site.bdNo}_MK ${site.siteName}`;
		setInvoiceName(invName);
	};

	/**
	 * Generate PDF with config
	 * @param {Sites array} sitesArr
	 */
	const generateInvoice = (selectedSite) => {
		const data = selectedSite.value;
		setBDname(data);
		const tariffData = data.tariffData;
		const tariffID = tariffData.tariffID.split("/")[0];
		const tariffType = tariffID.split("_");
		const tariffDesc = tariffData.tariffDesc;
		const EAEng = tariffData.EAEng.split("_");
		const EAThai = tariffData.EAThai.split("_");

		// Discount display string
		const discountON_display =
			data.discountON > 0
				? `- Discount ${parseFloat(formatNumber(data.discountON * 100, 2))}%`
				: "";
		const discountOFF_display =
			data.discountOFF > 0
				? `- Discount ${parseFloat(formatNumber(data.discountOFF * 100, 2))}%`
				: "";
		const discountFT_display =
			data.discountFT > 0
				? `- Discount ${parseFloat(formatNumber(data.discountFT * 100, 2))}%`
				: "";

		// Discounted Price
		const priceON_disc = solarCalc.calculateUnitCost(
			data.priceON,
			data.discountON,
			data.currency
		);
		const priceOFF_disc = solarCalc.calculateUnitCost(
			data.priceOFF,
			data.discountOFF,
			data.currency
		);
		const FTcost_disc = solarCalc.calculateUnitCost(
			data.FTcost,
			data.discountFT,
			data.currency
		);

		// Param to get Tariff sections
		const gyTariffParam = {
			discountON: data.discountON,
			discountOFF: data.discountOFF,
			discountFT: data.discountFT,
			priceON_disc,
			priceOFF_disc,
			FTcost_disc,
		};

		const GYTariff_remarks = getGYTariff(gyTariffParam, [EAEng[1], EAThai[1]]);

		// Param for invoice calculations
		const prod = {
			ON: data.solarON,
			OFF: data.solarOFF,
			TOTAL: data.solarTotal,
		};
		const price = {
			ON: data.priceON,
			OFF: data.priceOFF,
			FT: data.FTcost,
		};
		const discount = {
			ON: data.discountON,
			OFF: data.discountOFF,
			FT: data.discountFT,
		};

		// Savings and cost calculations results
		const savingsObj = solarCalc.calculateSavings(
			prod,
			price,
			discount,
			data.currency
		);
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			data.currency
		);

		// Savings
		const savings = savingsObj.savingsTotal;

		// Solar production
		const solarON = formatNumber(data.solarON, 3);
		const solarOFF = formatNumber(data.solarOFF, 3);
		const solarTotal = formatNumber(data.solarTotal, 3);

		// Prices
		const totalPriceON = costsObj.costON;
		const totalPriceOFF = costsObj.costOFF;
		const totalPriceFT = costsObj.costFT;

		// Subtotal price
		let subtotalPrice = costsObj.costTotal;

		// VAT calculations
		const currency = data.currency;
		const vatPercent = data.vat;
		const vatDisplay = parseNo(data.vat * 100);

		const VATObj = solarCalc.calculateVAT(subtotalPrice, currency, vatPercent);
		const VATPrice = VATObj.VAT;
		const tempTotal = VATObj.totalDue;

		let discountPrice = 0;
		let totalDue = 0;
		let specialDiscount_display = "";

		if (data.specialDiscount === null) totalDue = tempTotal;
		else {
			const specialDiscount = parseFloat(formatNumber(data.specialDiscount * 100, 2));
			specialDiscount_display = `Discount ${specialDiscount}%`;
			discountPrice = -(tempTotal * data.specialDiscount);

			totalDue =
				parseFloat(subtotalPrice.toFixed(2)) +
				parseFloat(VATPrice.toFixed(2)) +
				parseFloat(discountPrice.toFixed(2));
		}

		const docDefinition = {
			pageSize: "A4",
			pageMargins: 0,
			content: [
				{ nodeName: 'DIV', margin: [40, 40], style: 'billingDetailStyle', stack: [
				{
					nodeName: "DIV",
					stack: [
						{
							image: logo,
							width: 190,
						},
						{ text: "Billing Detail", style: "header_lg" },
						{ text: copyTypes[copyType], style: "sub_header_2", margin: 0 },
						{
							margin: [0, 0, 0, 25],
							table: {
								body: [
									[
										{
											text: "Attachment of Invoice No.",
											style: ["invoice_details"],
										},
										{ text: data.invNo, style: ["invoice_details"] },
									],
									[
										{
											text: "Billing Details No.",
											style: ["invoice_details"],
										},
										{ text: data.bdNo, style: ["invoice_details"] },
									],
									[
										{
											text: "Date",
											style: ["invoice_details"],
										},
										{ text: data.date, style: ["invoice_details"] },
									],
									[
										{
											text: "Site Name",
											style: ["invoice_details"],
										},
										{ text: data.siteName, style: ["invoice_details"] },
									],
								],
								widths: ["50%", "50%"],
							},
							layout: {
								defaultBorder: false,
							},
						},
					],
				},
				{
					style: "table_main",
					table: {
						body: [
							[
								{
									text: "DESCRIPTION",
									border: [false, true, false, true],
									bold: true,
									style: "table_header",
								},
								{
									border: [false, true, false, true],
									bold: true,
									text: "QUANTITY (kWh)",
									alignment: "right",
									style: "table_header",
								},
								{
									border: [false, true, false, true],
									bold: true,
									text: "UNIT COST",
									alignment: "right",
									style: "table_header",
								},
								{
									border: [false, true, false, true],
									bold: true,
									text: "TOTAL",
									alignment: "right",
									style: "table_header",
								},
							],
							[
								{
									text: `Period: ${data.dateStr}`,
									colSpan: 4,
									style: "table_main_rows",
									bold: true,
									margin: [0, 2, 0, 0],
								},
							],
							[
								{
									text: `Electricity Production during ${peakLabels[0]} Time ${discountON_display}`,
									style: "table_main_rows",
								},
								{
									text: solarON,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: priceON_disc.toFixed(6),
									style: "table_main_rows",
									alignment: "right",
								},
								{
									text: formatNumber(totalPriceON, 2),
									style: "table_main_rows",
									bold: true,
									alignment: "right",
								},
							],
							[
								{
									text: `Electricity Production during ${peakLabels[1]} Time ${discountOFF_display}`,
									style: "table_main_rows",
								},
								{
									text: solarOFF,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: priceOFF_disc.toFixed(6),
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: formatNumber(totalPriceOFF, 2),
									bold: true,
									alignment: "right",
									style: "table_main_rows",
								},
							],
							[
								{
									text: `Ft Cost ${discountFT_display}`,
									style: "table_main_rows",
									rowSpan: 2,
								},
								{
									text: solarTotal,
									alignment: "right",
									style: "table_main_rows",
									rowSpan: 2,
								},
								{
									text: FTcost_disc.toFixed(6),
									alignment: "right",
									style: "table_main_rows",
									rowSpan: 2,
								},
								{
									text: formatNumber(totalPriceFT, 2),
									bold: true,
									alignment: "right",
									style: "table_main_rows",
									rowSpan: 2,
								},
							],
							["EMPTY", "EMPTY", "EMPTY", "EMPTY"],
							[
								{
									colSpan: 3,
									text: "\nSubtotal",
									bold: true,
									alignment: "right",
									style: "table_main_rows",
								},
								"",
								"",
								{
									text: "\n" + formatNumber(subtotalPrice, 2),
									bold: true,
									alignment: "right",
									style: "table_main_rows",
								},
							],
							[
								{
									colSpan: 3,
									text: `VAT ${vatDisplay}%`,
									bold: true,
									alignment: "right",
									style: "table_main_rows",
								},
								"",
								"",
								{
									text: formatNumber(VATPrice, 2),
									bold: true,
									alignment: "right",
									style: "table_main_rows",
								},
							],
							[
								{
									colSpan: 3,
									text: specialDiscount_display,
									bold: true,
									alignment: "right",
								},
								"",
								"",
								{
									text: discountPrice < 0 ? formatNumber(discountPrice, 2) : "",
									bold: true,
									alignment: "right",
								},
							],
							[
								{
									colSpan: 3,
									text: "TOTAL DUE",
									bold: true,
									alignment: "right",
									border: [false, true, false, true],
									margin: [0, 1, 0, 0],
								},
								{ text: "", border: [false, true, false, true] },
								{ text: "", border: [false, true, false, true] },
								{
									text: formatNumber(totalDue, 2),
									bold: true,
									alignment: "right",
									border: [false, true, false, true],
									margin: [0, 1, 0, 0],
								},
							],
						],
						widths: ["55%", "15%", "15%", "15%"],
					},
					layout: {
						defaultBorder: false,
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 0;
						},
						paddingBottom: function (i, node) {
							return 0;
						},
					},
				},
				{
					nodeName: "DIV",
					stack: [
						{
							text: "* This invoice is exempted from income tax under BOI promotion and is therefore not subject to withholding tax.",
							color: "red",
						},
						{
							text: data.specialComment != null ? data.specialComment : "",
						},
					],
					margin: [0, 4, 0, 8],
				},
				{
					nodeName: "DIV",
					columns: [
						{
							stack: [
								{
									text: "About Electricity bills",
									style: {
										fontSize: 15,
										italics: true,
										bold: true,
									},
								},
								{
									text: "Based on the following electricity tariff",
									margin: [0, 1, 0, 0],
								},
								{
									text: `Time of use tariff [${tariffType[0]} Tariff]`,
									margin: [0, 1, 0, 0],
								},
								{
									text: `${tariffType[1]} for ${tariffDesc}`,
									margin: [0, 1, 0, 0],
								},
								{
									margin: [0, 2, 0, 0],
									text: `Definition of ${peakLabels[0]} hours`,
									style: "sub_header",
								},
								{
									margin: [0, 2, 0, 0],
									text: "Monday – Friday from 09.00 AM to 10.00 PM",
								},
								{
									margin: [0, 2, 0, 0],
									text: `Definition of ${peakLabels[1]} hours`,
									style: "sub_header",
								},
								{
									margin: [0, 2, 0, 0],
									text: "Monday – Friday from 10.00 PM to 09.00 AM",
								},
								{
									margin: [0, 2, 0, 0],
									text: "Saturday – Sunday , National Labor Day and normal public holiday",
								},
								{
									margin: [0, 2, 0, 0],
									text: "(excluding substitution holiday and Royal Ploughing Day) from 00.00 AM to 12.00 PM",
								},
								{
									margin: [0, 2, 0, 0],
									text: `${EAEng[0]} (${EAEng[1]}) tariff`,
									style: "sub_header",
								},
								{
									margin: [0, 2, 0, 0],
									text: `${peakLabels[0]} (baht/kWh) = ${data.priceON}`,
								},
								{
									margin: [0, 2, 0, 0],
									text: `${peakLabels[1]} (baht/kWh) = ${data.priceOFF}`,
								},
								{
									margin: [0, 2, 0, 0],
									text: `Current Ft during ${data.monthRange.engQuarter}`,
									style: "sub_header",
								},
								{
									margin: [0, 2, 0, 0],
									text: `${data.FTcost} baht/kWh.`,
								},
								{
									margin: [0, 2, 0, 0],
									text: "Green Yellow Tariff",
									style: "sub_header",
								},
								{
									margin: [0, 2, 0, 0],
									text: GYTariff_remarks.ONpeakStr_eng,
									style: { fontSize: 8 },
								},
								{
									margin: [0, 2, 0, 0],
									text: GYTariff_remarks.OFFpeakStr_eng,
									style: { fontSize: 8 },
								},
								{
									margin: [0, 2, 0, 0],
									text: GYTariff_remarks.FTStr_eng,
									style: { fontSize: 8 },
								},
								{
									text: `Savings generated compared to Grid In ${data.monthStr.engMonth}`,
									style: ["sub_header", "savings"],
									margin: [0, 5, 0, 0],
								},
								{ text: `฿${formatNumber(savings, 2)}`, style: "savings" },
							],
						},
						{
							stack: [
								{
									text: "เกี่ยวกับบิลค่าไฟฟ้า",
									style: {
										fontSize: 14,
										italics: true,
										font: "Taviraj",
									},
									margin: [0, -5, 0, 0],
								},
								{
									style: "thai",
									text: "อ้างอิงอัตราค่าไฟฟ้าดังต่อไปนี้",
									margin: [0, -4, 0, 0],
								},
								{
									style: "thai",
									text: `อัตราตามช่วงเวลาของการใช้ (${tariffType[0]})`,
									margin: [0, -4, 0, 0],
								},
								{
									style: "thai",
									text: `${tariffType[1]} สำหรับแรงดัน ${tariffDesc}`,
									margin: [0, -4, 0, 0],
								},
								{
									text: `กำหนดช่วงเวลา ${peakLabels[0]}`,
									style: ["sub_header", "thai"],
								},
								{
									style: "thai",
									text: "วันจันทร์ - วันศุกร์ ตั้งแต่เวลา 09.00น. จนถึง 22.00น.",
								},
								{
									text: `กำหนดช่วงเวลา ${peakLabels[1]}`,
									style: ["sub_header", "thai"],
								},
								{
									style: "thai",
									text: "วันเสาร์ - วันอาทิตย์, วันแรงงาน และวันหยุดราชการตามปกติ",
								},
								{
									style: "thai",
									text: "วันจันทร์ – วันศุกร์ ตั้งแต่เวลา",
								},
								{
									style: "thai",
									text: "22.00น. จนถึง 09.00น.",
								},
								{
									style: "thai",
									text: "(ไม่รวมวันหยุดชดเชยและวันพืชมงคล)",
								},
								{
									text: `${EAThai[0]} (${EAThai[1]}) ในปัจจุบัน`,
									style: ["sub_header", "thai"],
								},
								{
									style: "thai",
									text: `ช่วงเวลา ${peakLabels[0]} (บาท/kWh) = ${data.priceON}`,
								},
								{
									style: "thai",
									text: `ช่วงเวลา ${peakLabels[1]} (บาท/kWh) = ${data.priceOFF}`,
								},
								{
									text: `ค่า FT ในปัจจุบัน ${data.monthRange.thQuarter}`,
									style: ["sub_header", "thai"],
								},
								{
									style: "thai",
									text: `${data.FTcost} baht/kWh.`,
								},
								{
									text: "อัตราค่าไฟของ Green Yellow",
									style: ["sub_header", "thai"],
								},
								{
									style: ["thai", { fontSize: 7 }],
									text: GYTariff_remarks.ONpeakStr_th,
								},
								{
									style: ["thai", { fontSize: 7 }],
									text: GYTariff_remarks.OFFpeakStr_th,
								},
								{
									style: ["thai", { fontSize: 7 }],
									text: GYTariff_remarks.FTStr_th,
								},
								{
									text: `ผลประหยัดที่ได้ เมื่อเปรียบเทียบในเดือน ${data.monthStr.thMonth}`,
									style: ["sub_header", "savings", "thai"],
									margin: [0, 5, 0, 0],
								},
								{ text: `฿${formatNumber(savings, 2)}`, style: "savings" },
							],
						},
					],
					columnGap: 10,
				},
				// footer moved to page content for easy PDF merging in bulk download (1 click download)
				{
					nodeName: "DIV",
					absolutePosition: { x: 0, y: 800 },
					stack: [
						{
							text: [
								"This is a computer-generated Invoice. No signature is required"
							],
							style: {
								alignment: "center",
							},
						},
					]
				}
			]},
			],
			footer: [
				/* {
					columns: [
						{
							stack: [
								signature,
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: "Authorized by Green Yellow Solar1 (Thailand) Co., LTD.",
									style: "sub_signature",
								},
							],
							style: "signature",
						},
						{
							stack: [
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: "Received by: ___________________",
									style: "sub_signature",
								},
								{ text: "Date ....../....../......", style: "sub_signature" },
							],
							style: "signature",
						},
					],
					margin: [0, -80, 0, 0],
				}, */
				// {
				// 	stack: [
				// 		{
				// 			margin: [0, -40, 0, 0],
				// 			text: [
				// 				"This is a computer-generated Invoice. No signature is required"
				// 			],
				// 		},						
				// 	],
				// 	margin: [40, 20, 40, 0],
				// 	style: {
				// 		alignment: "center",
				// 	},
				// },
			],
			styles: {
				header_lg: {
					fontSize: 27,
				},
				sub_header: {
					decoration: "underline",
				},
				sub_header_2: {
					fontSize: 14,
					color: "#aaa",
					font: "Trirong",
					margin: [0, 0, 0, 0]
				},
				table_main: {
					margin: [0, 5, 0, 0],
				},
				savings: {
					color: "green",
					bold: true,
				},
				signature: {
					alignment: "center",
				},
				sub_signature: {
					margin: [0, 5, 0, 0],
				},
				table_header: {
					margin: [0, 1, 0, 0],
					fontSize: 10,
				},
				thai: {
					font: "Trirong",
					fontSize: 8,
					margin: [0, 0, 0, -3],
				},
				billingDetailStyle: {
					font: "Calibri",
					fontSize: 9,
				}
			},
			defaultStyle: {
				font: "Calibri",
				fontSize: 9,
			},
		};

		// notify parent that docDefinition is ready
		onDocDefinition(clone(docDefinition));

		const pdfDocGenerator = pdfMake.createPdf(docDefinition);
		setPDFDoc(docDefinition);

		pdfDocGenerator.getDataUrl((dataUrl) => {
			if (PDF == null) {
				setPDF(dataUrl);
			}
		});
	};

	/**
	 * Download PDF with allocated name
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${invoiceName}.pdf`);

	useMemo(() => {
		const labelsAreReady = !peakLabels.includes("");

		if (props.selectedSite != null && labelsAreReady)
			generateInvoice(props.selectedSite);

		return () => setInvoiceName(null);
	}, [props.selectedSite, peakLabels]);

	return (
		<>
			{PDF != null ? (
				<>
					<iframe style={{ width: "97%", height: "72vh" }} src={PDF} />
					<Button onClick={downloadPDF}>Download</Button>
				</>
			) : (
				<LoaderGraph style={{ height: "72vh" }} />
			)}
			<Button variant="light" onClick={props.closePreview} className="closeBtn">
				Close
			</Button>
		</>
	);
}

export default BillingDetailsTemplate;
